import cx from 'classnames'
import { Fragment, useState } from 'react'

import { Badge } from './badge.component'
import styles from './badge.gallery.module.css'
import type { BadgeShape, BadgeSize, BadgeVariant } from './badge.types'

const VARIANTS: BadgeVariant[] = [
  'highlight',
  'dark-1',
  'dark-2',
  'dark-3',
  'light-1',
  'light-2',
  'disabled',
  'info',
  'error',
  'alert',
  'success',
]

const SIZES: BadgeSize[] = ['xs', 's', 'm', 'l']

const SHAPES: BadgeShape[] = ['pill', 'rounded-square']

const variantColumns = `max-content repeat(${VARIANTS.length}, max-content)`
const sizeColumns = `max-content repeat(${SIZES.length}, max-content)`

const VariantGrid = ({ shape }: { shape: BadgeShape }) => (
  <div className={styles.scroller}>
    <div
      className={styles.grid}
      style={{ gridTemplateColumns: variantColumns }}
    >
      <span className={styles.corner} />
      {VARIANTS.map((variant) => (
        <span key={variant} className={styles.columnLabel}>
          {variant}
        </span>
      ))}

      {SIZES.map((size) => (
        <Fragment key={size}>
          <span className={styles.rowLabel}>{size}</span>
          {VARIANTS.map((variant) => (
            <span key={`${variant}-${size}`} className={styles.cell}>
              <Badge
                text="Badge"
                variant={variant}
                size={size}
                shape={shape}
                title={`${variant} / ${size} / ${shape}`}
              />
            </span>
          ))}
        </Fragment>
      ))}
    </div>
  </div>
)

/**
 * Development-only gallery rendering every Badge variant.
 *
 * Mirrors the Figma component set so the implementation can be compared
 * against the design side by side.
 *
 * @see https://www.figma.com/design/uoYtLe77tOoT3iN5hpDfv5/DS-Global-V3?node-id=10111-5086
 */
export const BadgeGallery = () => {
  const [showOutlines, setShowOutlines] = useState(false)

  return (
    <main className={cx(styles.page, showOutlines && styles.outlined)}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Design System V3</p>
        <h1 className={styles.title}>Badge</h1>
        <p className={styles.lede}>
          Every variant the component supports: {VARIANTS.length} variants
          &times; {SIZES.length} sizes &times; {SHAPES.length} shapes ={' '}
          {VARIANTS.length * SIZES.length * SHAPES.length} combinations. Hover
          any badge to see its exact variant.
        </p>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showOutlines}
            onChange={(event) => setShowOutlines(event.target.checked)}
          />
          Outline each badge box
        </label>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>In context</h2>
        <div className={styles.example}>
          <Badge text="New to order" variant="highlight" size="l" />
          <Badge
            text="Immediate Attention"
            variant="error"
            size="l"
            shape="rounded-square"
            icon="alert"
          />
        </div>
      </section>

      {SHAPES.map((shape) => (
        <section key={shape} className={styles.section}>
          <h2 className={styles.sectionTitle}>
            shape=<code className={styles.code}>{shape}</code>
          </h2>
          <VariantGrid shape={shape} />
        </section>
      ))}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Icon and icon position</h2>
        <div className={styles.scroller}>
          <div
            className={styles.grid}
            style={{ gridTemplateColumns: sizeColumns }}
          >
            <span className={styles.corner} />
            {SIZES.map((size) => (
              <span key={size} className={styles.columnLabel}>
                {size}
              </span>
            ))}

            <span className={styles.rowLabel}>icon left</span>
            {SIZES.map((size) => (
              <span key={`left-${size}`} className={styles.cell}>
                <Badge
                  text="Badge"
                  size={size}
                  icon="tick"
                  iconPosition="left"
                  title={`icon left / ${size}`}
                />
              </span>
            ))}

            <span className={styles.rowLabel}>icon right</span>
            {SIZES.map((size) => (
              <span key={`right-${size}`} className={styles.cell}>
                <Badge
                  text="Badge"
                  size={size}
                  icon="tick"
                  iconPosition="right"
                  title={`icon right / ${size}`}
                />
              </span>
            ))}

            <span className={styles.rowLabel}>no icon</span>
            {SIZES.map((size) => (
              <span key={`none-${size}`} className={styles.cell}>
                <Badge text="Badge" size={size} title={`no icon / ${size}`} />
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Edge cases</h2>
        <div className={styles.example}>
          <Badge text="A" title="single character" />
          <Badge
            text="A considerably longer badge label than usual"
            title="long label, should not wrap"
          />
          <Badge
            text="Consumer class"
            className={styles.consumerOverride}
            title="consumer className merges with the component's own"
          />
        </div>
      </section>
    </main>
  )
}
